import React from 'react';
import { Box } from '@mui/material';
import MainInfoForm, {
  MainInfoFormType,
} from './SparkBuilderSettingsFiles/MainInfoForm/MainInfoForm';
import ImagesAndMediaForm from './SparkBuilderSettingsFiles/ImagesAndMedia/ImagesAndMediaForm';
import PublishChecklistForm from './SparkBuilderSettingsFiles/PublishChecklist/PublishChecklistForm';
import { SparkTabs } from 'sharedComponents/UI';
import { useSparkBuilderStore } from 'stores';
import { useSparksByID } from 'hooks/spark';
import { useUpdateSpark } from 'hooks/spark/useUpdateSpark';
import { useBatchRemoveSparkCategory } from 'hooks/Categories';
import { useRemoveSparkImages } from 'hooks/batchOperations';
import { useAddBatchSparkImages } from 'hooks/batchOperations';
import { useAddBatchSparkCategories } from 'hooks/Categories/useAddBatchSparkCategories';
import { useFormUploadBatchImages } from 'hooks/batchOperations/useFormUploadBatchImages';
import type { ImagesAndMediaFormValues } from './SparkBuilderSettingsFiles/ImagesAndMedia/ImagesAndMediaForm';

const initialFormValues: MainInfoFormType = {
  name: '',
  headline: '',
  shortDescription: '',
  copyrightUrl: '',
  detailedDescription: '',
  marketingUrl: '',
  privacyPolicyUrl: '',
  supportUrl: '',
  mainCategoryID: '',
  subCategories: [],
  sparkPrivacyTypeID: '0',
  price: 0,
  billingTypeID: '',
  trialUses: 0,
};

export type initialFormValuesType = typeof initialFormValues;

function SparkBuilderSettings() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const sparkID = useSparkBuilderStore((store) => store.sparkID);
  const { getUrlWithSortOrder } = useFormUploadBatchImages('app-assets');

  const { joinedSpark, isLoading } = useSparksByID(sparkID);

  const { mutateAsync: updateSpark } = useUpdateSpark(joinedSpark?.id);

  const { mutateAsync: removeSparkCategories } = useBatchRemoveSparkCategory();
  const { mutateAsync: addSparkCategories } = useAddBatchSparkCategories(
    joinedSpark?.id
  );

  const { mutateAsync: addSparkImages } = useAddBatchSparkImages(
    joinedSpark?.id
  );
  const { mutateAsync: removeSparkImages } = useRemoveSparkImages(
    joinedSpark?.id
  );

  const handleMainInfoSave = async (values: MainInfoFormType) => {
    await updateSpark({
      sparkPrivacyTypeID: +values.sparkPrivacyTypeID,
      price:
        values.sparkPrivacyTypeID === '1'
          ? null
          : values.price
            ? +values.price
            : undefined,
      sparkBillingTypeID:
        values.sparkPrivacyTypeID === '1'
          ? null
          : values.billingTypeID === '0' || Number(values.price) === 0
            ? null
            : +values.billingTypeID,
      nameforHuman: values.name,
      descriptionforHuman: values.shortDescription,
      headline: values.headline,
      trialUses: values.sparkPrivacyTypeID === '1' ? 0 : +values.trialUses,
      supportURL: values.sparkPrivacyTypeID === '1' ? null : values.supportUrl,
      marketingURL:
        values.sparkPrivacyTypeID === '1' ? null : values.marketingUrl,
      privacyPolicyURL:
        values.sparkPrivacyTypeID === '1' ? null : values.privacyPolicyUrl,
      copyright: values.sparkPrivacyTypeID === '1' ? null : values.copyrightUrl,
      overview: values.detailedDescription,
    });
    const oldSparkCategoryIDs = joinedSpark?.sparkCategories?.map(
      (category) => category.id
    );
    if (oldSparkCategoryIDs?.length) {
      await removeSparkCategories({ categoryIDs: oldSparkCategoryIDs });
    }
    const { mainCategoryID, subCategories } = values;
    if (!!mainCategoryID) {
      await addSparkCategories([
        { categoryID: mainCategoryID, mainCategory: true },
        ...subCategories.map((category) => ({
          categoryID: category,
          mainCategory: false,
        })),
      ]);
    }
  };

  const onSparkTabChange = (tabIndex: number) => {
    setSelectedItemIndex(tabIndex);
  };

  const onChangeThumbnail = async (imageURL: string) => {
    await updateSpark({
      iconURL: imageURL,
    });
  };

  const onImagesAndMediaSave = async (values: ImagesAndMediaFormValues) => {
    await updateSpark({
      videoURL: values.videoURL,
      iconURL: values.imageURL,
    });
    const { sparkImages } = values;

    const oldSparkImageIDs = joinedSpark?.sparkImages?.map((image) => image.id);

    if (sparkImages.length) {
      const imagesWithSortOrder = await getUrlWithSortOrder(sparkImages);

      if (oldSparkImageIDs?.length) {
        await removeSparkImages({ sparkImageIDs: oldSparkImageIDs });
      }
      await addSparkImages({ sparkImageUrls: imagesWithSortOrder });
    } else if (!sparkImages.length && oldSparkImageIDs?.length) {
      await removeSparkImages({ sparkImageIDs: oldSparkImageIDs });
    }
  };

  const tabItems = [
    {
      label: 'Info',
      content: (
        <MainInfoForm
          joinedSpark={joinedSpark}
          setSelectedItemIndex={setSelectedItemIndex}
          onSave={handleMainInfoSave}
          isLoading={isLoading}
        />
      ),
    },
    {
      label: 'Images & Media',
      content: (
        <ImagesAndMediaForm
          onSave={onImagesAndMediaSave}
          joinedSpark={joinedSpark}
          setSelectedItemIndex={setSelectedItemIndex}
          onChangeThumbnail={onChangeThumbnail}
        />
      ),
    },
    {
      label: 'Publish Checklist',
      content: <PublishChecklistForm sparkID={sparkID} />,
    },
  ];

  return (
    <Box position='relative' height='100%'>
      <SparkTabs
        tabItems={tabItems}
        selectedItemIndex={selectedItemIndex}
        onChange={onSparkTabChange}
        tabPanelStyles={{
          height: '100%',
          padding: '0px',
          position: 'relative',
        }}
      />
    </Box>
  );
}

export default SparkBuilderSettings;
